'use client';

import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checklist, ChecklistCompletion, ChecklistItem } from '../types/checklist.types';
import { useTenancy } from '@/contexts/TenancyContext';
import { cn } from '@/lib/utils';
import { getDocument } from '@/services/documentsService';
import { cleanDateAndTimeObject, toLocalDateString, renderDate } from '@shared/renderDate';
import { Button } from '@/components/ui/button';

interface ChecklistHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklist: Checklist;
  items: ChecklistItem[];
}

function EvidenceCard({
  doc,
  docId,
  fallbackLabel,
}: {
  doc: any;
  docId: string;
  fallbackLabel: string;
}) {
  const { userIdToName } = useTenancy();
  const fileName = doc?.fileName || doc?.name || fallbackLabel || `Document ${docId}`;
  const documentType = doc?.documentType || doc?.type || 'Evidence';
  const classification = doc?.classification || 'Business Confidential';
  const uploadedBy = doc?.uploadedBy || doc?.owner || doc?.uploadedByName;
  const uploadedAtRaw = doc?.dateUploaded || doc?.dateAdded || doc?.createdDateTime;
  const uploadedAt = uploadedAtRaw ? cleanDateAndTimeObject(uploadedAtRaw) : null;
  const status = doc?.status;
  const resolvedDocId = doc?.docID || doc?.id || docId;
  const webUrl = doc?.url || doc?.webUrl || doc?.sharepointUrl;

  return (
    <div className="flex h-full flex-col justify-between rounded-xl border border-border/60 bg-card/90 p-4 shadow-sm shadow-primary/5">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
            {documentType}
          </Badge>
          {status && (
            <Badge variant="secondary" className="border-border bg-muted/70 text-foreground">
              {status}
            </Badge>
          )}
        </div>

        <div className="space-y-1">
          <p className="line-clamp-2 text-base font-semibold text-foreground" title={fileName}>{fileName}</p>
          <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground/70">{classification}</p>
        </div>

        <dl className="grid gap-2 text-xs text-muted-foreground">
          <div className="flex items-center justify-between gap-2">
            <dt className="uppercase tracking-[0.14em] text-muted-foreground/70">Document ID</dt>
            <dd className="font-medium text-foreground">{resolvedDocId}</dd>
          </div>
          {uploadedBy && (
            <div className="flex items-center justify-between gap-2">
              <dt className="uppercase tracking-[0.14em] text-muted-foreground/70">Uploaded By</dt>
              <dd className="truncate text-foreground" title={uploadedBy}>{userIdToName[uploadedBy] || uploadedBy}</dd>
            </div>
          )}
          {uploadedAt && !Number.isNaN(uploadedAt.getTime()) && (
            <div className="flex items-center justify-between gap-2">
              <dt className="uppercase tracking-[0.14em] text-muted-foreground/70">Uploaded</dt>
              <dd className="text-foreground">{toLocalDateString(uploadedAt)}</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="mt-4 flex items-center justify-center">
        <code className="rounded bg-muted/40 px-2 py-1 text-xs text-muted-foreground hidden" title={fallbackLabel || docId}>
          {fallbackLabel || docId}
        </code>
        <Button variant="outline" onClick={() => window.open(`/documentdetail/${docId}`, '_blank')}>
          View Document
        </Button>
      </div>
    </div>
  );
}

export function ChecklistHistoryDialog({
  open,
  onOpenChange,
  checklist,
  items
}: ChecklistHistoryDialogProps) {
  const { userIdToName } = useTenancy();
  const [selectedCompletion, setSelectedCompletion] = useState<ChecklistCompletion | null>(null);
  const [timelineVisible, setTimelineVisible] = useState(true);
  const [detailAnimationKey, setDetailAnimationKey] = useState(0);

  const frostedCardClasses = 'rounded-2xl border border-border/70 bg-card/95 shadow-lg shadow-primary/10 backdrop-blur';
  const timelineEntryClasses = 'relative flex w-full cursor-pointer flex-col gap-2 rounded-xl border border-border/60 bg-muted/20 p-4 text-left transition-all duration-200 ease-out hover:border-primary/40 hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none';

  const completions = useMemo(() => {
    if (!Array.isArray(checklist?.completionHistory)) return [];
    return checklist.completionHistory
      .slice()
      .sort((a, b) => {
        const aDate = a.completedAt instanceof Date ? a.completedAt.getTime() : new Date(a.completedAt ?? 0).getTime();
        const bDate = b.completedAt instanceof Date ? b.completedAt.getTime() : new Date(b.completedAt ?? 0).getTime();
        return bDate - aDate;
      });
  }, [checklist?.completionHistory]);

  useEffect(() => {
    if (open) {
      setSelectedCompletion(null);
      setTimelineVisible(true);
      setDetailAnimationKey(prev => prev + 1);
    } else {
      setSelectedCompletion(null);
    }
  }, [open, completions]);

  useEffect(() => {
    if (selectedCompletion) {
      setDetailAnimationKey(prev => prev + 1);
    }
  }, [selectedCompletion]);

  if (!checklist) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl! max-h-[80vh] overflow-y-auto overflow-x-hidden border border-border/80 bg-card/95 p-0 shadow-2xl">
        <div
          className={cn(
            'grid w-full gap-0 transition-[grid-template-columns] duration-300 ease-out',
            timelineVisible
              ? 'lg:grid-cols-[minmax(260px,1fr),minmax(420px,1.5fr)]'
              : 'lg:grid-cols-1'
          )}
        >
          <aside
            className={cn(
              'hidden h-full flex-col border-r border-border/60 bg-muted/20/80 p-6 transition-all duration-300 ease-out',
              timelineVisible ? 'lg:flex translate-x-0 opacity-100' : 'lg:hidden -translate-x-6 opacity-0'
            )}
          >
            <div className="mb-6 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/70">Completion History</p>
              <h2 className="text-xl font-semibold text-foreground">{checklist.title}</h2>
              <p className="text-sm text-muted-foreground">
                Select a completion to review responses, evidence, and linked findings.
              </p>
            </div>

            {completions.length > 0 ? (
              <ol className="flex-1 space-y-3 overflow-y-auto pr-2">
                {completions.map((entry, idx) => {
                  const isSelected = entry === selectedCompletion;
                  const completedAt = entry.completedAt instanceof Date
                    ? entry.completedAt
                    : new Date(entry.completedAt ?? 0);
                  const hasFindings = Array.isArray(entry.findings) && entry.findings.length > 0;
                  return (
                    <li key={idx}>
                      <button
                        type="button"
                        className={cn(
                          timelineEntryClasses,
                          isSelected && 'border-primary/60 bg-primary/10 shadow-lg shadow-primary/20'
                        )}
                        onClick={() => {
                          setSelectedCompletion(entry);
                          setTimelineVisible(false);
                        }}
                      >
                        <div className="flex items-center justify-between gap-3 text-left">
                          <span className="font-medium text-foreground">
                            {entry.completedBy ? (userIdToName[entry.completedBy] || entry.completedBy) : 'Unknown user'}
                          </span>
                          <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                            {completedAt.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          {entry.signature && (
                            <Badge variant="outline" className="border-emerald-300/70 bg-emerald-100/10 text-emerald-400">
                              Signed
                            </Badge>
                          )}
                          {hasFindings && (
                            <Badge variant="outline" className="border-amber-300/70 bg-amber-100/10 text-amber-400">
                              {entry.findings!.length} finding{entry.findings!.length === 1 ? '' : 's'}
                            </Badge>
                          )}
                          {entry.values && Object.keys(entry.values).length > 0 && (
                            <Badge variant="secondary" className="border-border bg-muted/80 text-foreground">
                              {Object.keys(entry.values).length} response{Object.keys(entry.values).length === 1 ? '' : 's'}
                            </Badge>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border/60 bg-muted/10 p-6 text-sm text-muted-foreground">
                No completion history available yet.
              </div>
            )}
          </aside>

          <section className="flex min-h-[460px] flex-col gap-5 p-6 transition-all duration-300 ease-out lg:p-8">
            <DialogHeader className="space-y-2 lg:hidden">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/70">Completion History</p>
              <DialogTitle className="text-xl">{checklist.title}</DialogTitle>
            </DialogHeader>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {!timelineVisible && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setTimelineVisible(true);
                      setSelectedCompletion(null);
                    }}
                  >
                    ← Back to history
                  </Button>
                )}
              </div>
              {/* {selectedCompletion && (
                <Button variant="ghost" size="sm" onClick={() => setTimelineVisible(prev => !prev)}>
                  {timelineVisible ? 'Hide history' : 'Show history'}
                </Button>
              )} */}
            </div>

            {completions.length === 0 && (
              <div className={cn(frostedCardClasses, 'flex flex-1 items-center justify-center p-10 text-center text-muted-foreground')}>
                No completion history available yet.
              </div>
            )}

            {completions.length > 0 && !selectedCompletion && (
              <div
                className={cn(
                  frostedCardClasses,
                  'flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center text-muted-foreground animate-in fade-in slide-in-from-bottom-4'
                )}
              >
                <span className="text-sm">Select a completion from the list to view details.</span>
                <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80">
                  Completion details will appear here
                </span>
              </div>
            )}

            {selectedCompletion && (
              <SelectedCompletionDetails
                key={detailAnimationKey}
                entry={selectedCompletion}
                items={items}
                frostedCardClasses={frostedCardClasses}
                userIdToName={userIdToName}
              />
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SelectedCompletionDetails({
  entry,
  items,
  frostedCardClasses,
  userIdToName,
}: {
  entry: ChecklistCompletion;
  items: ChecklistItem[];
  frostedCardClasses: string;
  userIdToName: Record<string, string>;
}) {
  const completedAt = entry.completedAt instanceof Date
    ? entry.completedAt
    : new Date(entry.completedAt ?? 0);

  const [evidenceDocs, setEvidenceDocs] = useState<Record<string, any>>({});
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);

  // DED-1113: Helper to check if a value looks like a date/timestamp
  const isDateLike = (val: unknown): boolean => {
    if (val instanceof Date) return true;
    if (typeof val !== 'object' || val === null) return false;
    const obj = val as Record<string, unknown>;
    // Firestore Timestamp
    if (typeof obj.toDate === 'function') return true;
    // Raw Firestore timestamp with seconds/nanoseconds
    if (typeof obj.seconds === 'number' || typeof obj._seconds === 'number') return true;
    // __time__ wrapper
    if (obj.__time__ && typeof obj.__time__ === 'object') return true;
    return false;
  };

  const renderValue = (raw: unknown): ReactNode => {
    if (raw === null || raw === undefined || raw === '') {
      return <span className="italic text-muted-foreground">Not answered</span>;
    }
    if (typeof raw === 'boolean') {
      return <span>{raw ? 'Yes' : 'No'}</span>;
    }
    // DED-1113: Handle Date objects and Firestore Timestamps
    if (raw instanceof Date) {
      return <span className="break-words">{toLocalDateString(raw, true) || raw.toLocaleString()}</span>;
    }
    // Check for date-like objects (Firestore Timestamps, etc.)
    if (isDateLike(raw)) {
      const formatted = renderDate(raw);
      if (formatted && formatted !== 'Invalid date') {
        return <span className="break-words">{formatted}</span>;
      }
    }
    if (Array.isArray(raw)) {
      return <span className="break-words">{raw.map(value => (typeof value === 'object' ? JSON.stringify(value) : String(value))).join(', ')}</span>;
    }
    if (typeof raw === 'object') {
      return (
        <pre className="whitespace-pre-wrap break-words rounded-lg border border-border/50 bg-muted/40 p-3 text-xs text-muted-foreground max-w-full overflow-auto">
          {JSON.stringify(raw, null, 2)}
        </pre>
      );
    }
    return <span className="break-words">{String(raw)}</span>;
  };

  // DED-1113: Ensure findings are properly typed and handle object serialization
  const findings = Array.isArray(entry.findings) ? entry.findings.map(f => {
    // Handle case where finding might be stored as a raw object or string
    if (typeof f === 'string') {
      return { id: f, type: 'Unknown', status: 'Open' as const, summary: f, owner: '', correctiveActions: [], origin: 'standalone' as const };
    }
    return f;
  }) : [];

  const parseEvidenceValue = (value: unknown): { docId: string | null; display: string } => {
    if (value === null || value === undefined) {
      return { docId: null, display: '' };
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return { docId: trimmed || null, display: trimmed };
    }
    if (typeof value === 'number') {
      const str = String(value);
      return { docId: str, display: str };
    }
    if (typeof value === 'object') {
      const cast = value as Record<string, unknown>;
      const possibleId = cast.docID || cast.documentId || cast.id || cast.value;
      const docId = typeof possibleId === 'string' ? possibleId : typeof possibleId === 'number' ? String(possibleId) : null;
      const display = cast.displayName || cast.fileName || cast.name || cast.label || cast.title || (docId ?? JSON.stringify(value));
      return { docId, display: String(display ?? '') };
    }
    return { docId: null, display: String(value) };
  };

  type EvidenceEntry = {
    key: string;
    rawValue: unknown;
    docId: string;
    display: string;
  };

  const evidenceEntries = useMemo<EvidenceEntry[]>(() => {
    if (!entry.values) return [];
    const entries: EvidenceEntry[] = [];
    for (const [key, value] of Object.entries(entry.values)) {
      if (!key.startsWith('evidence-')) continue;
      const { docId, display } = parseEvidenceValue(value);
      if (!docId) continue;
      entries.push({ key, rawValue: value, docId, display: display || docId });
    }
    return entries;
  }, [entry.values]);

  useEffect(() => {
    let cancelled = false;
    if (!evidenceEntries.length) {
      setEvidenceDocs({});
      setEvidenceError(null);
      return;
    }

    const loadEvidence = async () => {
      setLoadingEvidence(true);
      setEvidenceError(null);
      try {
        const results: Record<string, any> = {};
        await Promise.all(evidenceEntries.map(async ({ key, docId }) => {
          try {
            const doc = await getDocument(docId);
            if (doc) {
              results[key] = doc;
            }
          } catch (error) {
            console.error('[ChecklistHistoryDialog] Failed to load evidence document', docId, error);
          }
        }));
        if (!cancelled) {
          setEvidenceDocs(results);
        }
      } catch (error: any) {
        if (!cancelled) {
          setEvidenceError(error?.message ?? 'Failed to load evidence documents');
        }
      } finally {
        if (!cancelled) {
          setLoadingEvidence(false);
        }
      }
    };

    void loadEvidence();

    return () => {
      cancelled = true;
    };
  }, [evidenceEntries]);

  return (
    <div className="space-y-6 w-full max-w-4xl mx-auto transition-all duration-300 ease-out animate-in fade-in slide-in-from-right-4">
      <div className={cn(frostedCardClasses, 'p-6 space-y-4')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/80">Completed By</p>
            <p className="text-base font-medium text-foreground">
              {entry.completedBy ? (userIdToName[entry.completedBy] || entry.completedBy) : 'Unknown user'}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/80">Completed At</p>
            <p className="text-base font-medium text-foreground">{completedAt.toLocaleString()}</p>
          </div>
        </div>

        {entry.signature && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/80">Signature</p>
            <div className="flex items-center justify-center rounded-xl border border-border/60 bg-muted/30 p-4">
              <img
                src={entry.signature}
                alt="Signature"
                className="max-h-40 w-auto object-contain"
              />
            </div>
          </div>
        )}
      </div>

      {entry.values && Object.keys(entry.values).length > 0 && (
        <div className={cn(frostedCardClasses, 'p-6 space-y-4')}>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/80">Responses</p>
              <p className="text-sm text-muted-foreground">Captured answers for each checklist item.</p>
            </div>
            <Badge variant="secondary" className="border-border bg-muted/60 text-foreground">
              {Object.keys(entry.values).length} item{Object.keys(entry.values).length === 1 ? '' : 's'}
            </Badge>
          </div>

          <div className="space-y-3">
            {/* DED-1113: Sort entries by item order to maintain consistent ordering */}
            {Object.entries(entry.values)
              .map(([itemId, value]) => {
                const item = items.find(i => i.id === itemId);
                return { itemId, value, item, order: item?.order ?? Infinity };
              })
              .sort((a, b) => a.order - b.order)
              .map(({ itemId, value, item }) => {
              const v: any = value as any;
              const val = (v && typeof v === 'object' && 'value' in v) ? v.value : v;
              const titleOverride = (v && typeof v === 'object' && 'title' in v) ? v.title : undefined;
              const title = titleOverride || item?.title || itemId;

              if (title === "findings" || title.startsWith("evidence-item-")) {
                return null;
              }

              return (
                <div
                  key={itemId}
                  className="rounded-xl border border-border/60 bg-card/90 px-4 py-3 shadow-sm shadow-primary/5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-foreground">{title}</p>
                    {item?.requireEvidence && (
                      <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
                        Evidence required
                      </Badge>
                    )}
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {renderValue(val)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {evidenceEntries.length > 0 && (
        <div className={cn(frostedCardClasses, 'p-6 space-y-4')}>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/80">Evidence</p>
              <p className="text-sm text-muted-foreground">Documents uploaded with this completion.</p>
            </div>
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
              {evidenceEntries.length} item{evidenceEntries.length === 1 ? '' : 's'}
            </Badge>
          </div>

          {loadingEvidence && (
            <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 px-4 py-3 text-sm text-muted-foreground">
              Loading evidence documents…
            </div>
          )}

          {evidenceError && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {evidenceError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {evidenceEntries.map(({ key, docId, display }) => {
              const doc = evidenceDocs[key];
              const fallbackLabel = display || docId || key;
              return (
                <EvidenceCard
                  key={key}
                  doc={doc}
                  docId={docId}
                  fallbackLabel={fallbackLabel}
                />
              );
            })}
          </div>
        </div>
      )}

      {findings.length > 0 && (
        <div className={cn(frostedCardClasses, 'p-6 space-y-4')}>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/80">Findings</p>
              <p className="text-sm text-muted-foreground">Linked findings captured during completion.</p>
            </div>
            <Badge variant="outline" className="border-amber-300/70 bg-amber-100/10 text-amber-400">
              {findings.length} item{findings.length === 1 ? '' : 's'}
            </Badge>
          </div>

          <div className="space-y-3">
            {findings.map((finding, index) => (
              <div
                key={finding.id ?? index}
                className="rounded-xl border border-border/60 bg-card/90 px-4 py-4 shadow-sm shadow-primary/5"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
                    {typeof finding.type === 'object' ? JSON.stringify(finding.type) : (finding.type || 'Unknown')}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      'border-border/50 bg-muted/60 uppercase tracking-[0.16em]',
                      finding.status === 'Closed'
                        ? 'border-emerald-300/70 bg-emerald-100/10 text-emerald-400'
                        : 'border-amber-300/70 bg-amber-100/10 text-amber-400'
                    )}
                  >
                    {finding.status}
                  </Badge>
                </div>

                <p className="mt-3 text-sm font-semibold text-foreground">
                  {typeof finding.summary === 'object' ? JSON.stringify(finding.summary) : (finding.summary || 'Untitled finding')}
                </p>

                {finding.comments && (
                  <p className="mt-2 text-sm text-muted-foreground">{finding.comments}</p>
                )}

                <dl className="mt-3 grid gap-3 text-xs text-muted-foreground sm:grid-cols-2">
                  {finding.owner && (
                    <div>
                      <dt className="font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">Owner</dt>
                      <dd className="mt-1 text-sm text-foreground">
                        {userIdToName[finding.owner] || finding.owner}
                      </dd>
                    </div>
                  )}
                  {finding.createdAt && (
                    <div>
                      <dt className="font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">Created</dt>
                      <dd className="mt-1 text-sm text-foreground">
                        {toLocalDateString(cleanDateAndTimeObject(finding.createdAt as any))}
                      </dd>
                    </div>
                  )}
                  {finding.updatedAt && (
                    <div>
                      <dt className="font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">Updated</dt>
                      <dd className="mt-1 text-sm text-foreground">
                        {toLocalDateString(cleanDateAndTimeObject(finding.updatedAt as any))}
                      </dd>
                    </div>
                  )}
                  {finding.correctiveActions && finding.correctiveActions.length > 0 && (
                    <div>
                      <dt className="font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">Corrective Actions</dt>
                      <dd className="mt-1 text-sm text-foreground">
                        {finding.correctiveActions.length} item{finding.correctiveActions.length === 1 ? '' : 's'}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
